import type { AxisSpec, StreamEntry } from "../../ChartSpec";
import type { PreparedSeries } from "../prepare-series";

/** Intl + toFixed only accept 0–20 fraction digits; log10(0)/tiny steps blow past that. */
function fractionDigitsForStep(step: number): number {
    if (!(step > 0) || !Number.isFinite(step)) return 0;
    return Math.min(20, Math.max(0, -Math.floor(Math.log10(step))));
}

const tickFormatters = new Map<number, Intl.NumberFormat>();

/** Building an Intl formatter is costly, and we format every tick on every axis on every resize — so reuse them. */
function tickFormatterForDecimals(decimals: number): Intl.NumberFormat {
    const cached = tickFormatters.get(decimals);
    if (cached) return cached;

    // Match tooltip locale (en-US) so axis labels and tooltips don't disagree across environments.
    const formatter = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
    tickFormatters.set(decimals, formatter);
    return formatter;
}

/** Format a tick value using the decimal precision implied by the tick step. */
export function formatTickLabel(value: number, step: number): string {
    return tickFormatterForDecimals(fractionDigitsForStep(step)).format(value);
}

/** Trim float noise (0.1 + 0.2 → 0.30000000000000004) down to the precision the step actually implies. */
function roundForStep(value: number, step: number): number {
    return Number(value.toFixed(fractionDigitsForStep(step)));
}

type NiceTicks = { niceMin: number; niceMax: number; step: number; ticks: number[] };

/** Target number of y-axis ticks for a given plot height: roughly one tick per 48px, clamped. */
export function targetTickCountForHeight(plotHeightPx: number): number {
    return Math.min(8, Math.max(2, Math.floor(plotHeightPx / 48)));
}

/** Pick 1, 2, or 5 × 10ⁿ — same “nice number” idea as d3’s tick step heuristics. */
function niceStepMultiplier(err: number): number {
    if (err >= Math.sqrt(50)) return 10;
    if (err >= Math.sqrt(10)) return 5;
    if (err >= Math.sqrt(2)) return 2;
    return 1;
}

/** Pick a round tick step and expand the extent outward to whole steps. */
export function computeNiceTicks(min: number, max: number, targetCount: number): NiceTicks {
    let paddedMin = min;
    let paddedMax = max;

    if (min === max) {
        const pad = Math.max(Math.abs(min), 1) * 0.05;
        paddedMin = min - pad;
        paddedMax = max + pad;
    }

    const clampedTargetCount = Math.max(1, targetCount);
    const step0 = (paddedMax - paddedMin) / clampedTargetCount;
    // Clamp before log10 so a zero/underflow span can't produce -Infinity → NaN step.
    const base = 10 ** Math.floor(Math.log10(Math.max(step0, Number.MIN_VALUE)));
    const err = step0 / base;
    const rawStep = base * niceStepMultiplier(err);
    const step = Number.isFinite(rawStep) && rawStep > 0 ? rawStep : 1;

    const niceMin = roundForStep(Math.floor(paddedMin / step) * step, step);
    const niceMax = roundForStep(Math.ceil(paddedMax / step) * step, step);

    const tickCount = Math.round((niceMax - niceMin) / step);
    const ticks: number[] = [];
    for (let i = 0; i <= tickCount; i++) {
        ticks.push(roundForStep(niceMin + i * step, step));
    }

    return { niceMin, niceMax, step, ticks };
}

type AxisExtent = { min: number; max: number };

/** Axes carrying at least one bar series. Those read against zero, so they scale differently. */
export function getBarAxisIds(streams: StreamEntry[]): Set<string> {
    return new Set(streams.filter((stream) => stream.seriesType === "bar").map((stream) => stream.axisId));
}

/** Data min/max per axis — no padding; callers add headroom separately. Axes with bars include zero. */
export function computeAxisExtents(
    series: PreparedSeries[],
    streams: StreamEntry[],
    axes: AxisSpec[],
): Map<string, AxisExtent> {
    const axisIdByStreamId = new Map(streams.map((stream) => [stream.streamId, stream.axisId]));
    const barAxisIds = getBarAxisIds(streams);
    const knownAxisIds = new Set(axes.map((axis) => axis.id));
    const extents = new Map<string, AxisExtent>();

    // One pass over the data, bucketed by axis. An axis only gets an entry once it has seen a real
    // number, so "no entry" means "this axis has no data" — the caller then leaves min/max off and
    // lets ECharts auto-range, since nothing is plotted there anyway.
    series.forEach((matchedSeries) => {
        const axisId = axisIdByStreamId.get(matchedSeries.streamId);
        if (axisId === undefined || !knownAxisIds.has(axisId)) return;

        matchedSeries.points.forEach(([, value]) => {
            if (value == null || !Number.isFinite(value)) return;

            const extent = extents.get(axisId);
            if (!extent) {
                extents.set(axisId, { min: value, max: value });
                return;
            }
            if (value < extent.min) extent.min = value;
            if (value > extent.max) extent.max = value;
        });
    });

    // A bar's height is read against zero, so an axis with bars has to reach zero. Stretching here
    // rather than in the loop leaves an axis with no data without an entry.
    barAxisIds.forEach((axisId) => {
        const extent = extents.get(axisId);
        if (!extent) return;
        if (extent.min > 0) extent.min = 0;
        if (extent.max < 0) extent.max = 0;
    });

    return extents;
}

export type AxisScale = {
    niceMin: number;
    niceMax: number;
    step: number;
    ticks: number[];
    maxVisibleTick: number;
};

/**
 * Compute nice ticks for the data, then bump the y-axis max so the unit badge fits above the top
 * visible tick.
 *
 * Linear scale: empty pixels / plot height = empty axis range / axis span, so:
 *   (axisMax - topTick) / (axisMax - niceMin) = headroomPx / plotHeightPx
 *
 * `axisMax` is left at that exact value (not rounded up to a tick). Headroom labels are already
 * blank, so a non-nice ceiling never shows — and the badge-to-tick gap stays a fixed pixel count
 * instead of jumping by whole tick steps.
 *
 * `maxVisibleTick` is the nice upper bound for the raw data extent. Callers hide tick labels above
 * it so the badge band looks empty; ECharts still draws ticks at `step` for grid math.
 *
 * When headroomPx ≥ plotHeightPx (`f >= 1`), the proportional bump isn’t meaningful — fall back to
 * plain nice ticks for the data extent (tiny / first-measure panels).
 */
export function computeAxisScale(input: {
    extent: { min: number; max: number };
    plotHeightPx: number;
    headroomPx: number;
    targetCount: number;
    /** Set for axes carrying bars: zero stays at the bottom instead of being centred. */
    zeroBaseline?: boolean;
}): AxisScale {
    const { extent, plotHeightPx, headroomPx, targetCount, zeroBaseline = false } = input;
    let { min, max } = extent;

    if (max <= min) {
        // A flat series has no range to draw, so invent one. Padding both ways puts the value in the
        // middle of the plot, which is what a line wants. A flat-zero bar series would then hang its
        // zero-height bars across the middle and read as data, so give those an upward range only.
        const pad = Math.max(Math.abs(min), 1) * 0.05;
        if (zeroBaseline && min >= 0) {
            min = 0;
            max = Math.max(Math.abs(max), 1);
        } else {
            min -= pad;
            max += pad;
        }
    }

    const f = headroomPx / Math.max(plotHeightPx, 1);
    const dataTicks = computeNiceTicks(min, max, targetCount);
    const { niceMin, niceMax: maxVisibleTick, step, ticks } = dataTicks;

    if (f >= 1) {
        return { ...dataTicks, maxVisibleTick };
    }

    // Exact headroom — do not ceil to the next step, or EXTRA_PX becomes a non-linear step-crossing
    // knob instead of a pixel gap.
    const niceMax = (maxVisibleTick - f * niceMin) / (1 - f);

    return { niceMin, niceMax, step, ticks, maxVisibleTick };
}
